import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * How long to keep waiting for a hash target to appear before giving up and
 * going to the top of the page instead.
 */
const HASH_TARGET_TIMEOUT_MS = 2_000;

/**
 * Restores scroll position on navigation, and honours in-page `#hash` links.
 *
 * React Router does not do this: without it, navigating from halfway down the
 * landing page to another route lands the user halfway down the new page, which
 * reads as a broken link.
 *
 * Hash targets need more than a single lookup. Pages are lazily loaded, so on a
 * cold load of `/#companies` this effect runs before the page chunk has mounted
 * and the element does not exist yet. Rather than silently scrolling to the top —
 * which is what a shared deep link would do — we watch for the element and scroll
 * as soon as it appears, with a deadline so a bad hash still settles at the top.
 */
export function ScrollRestoration() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // Smoothness is opted into here rather than set globally in CSS, and it is
    // dropped for users who asked for reduced motion.
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const hashBehavior: ScrollBehavior = prefersReducedMotion ? 'instant' : 'smooth';

    const toTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    };

    if (!hash) {
      toTop();
      return undefined;
    }

    const scrollToHash = (): boolean => {
      // An invalid selector (`#1foo`) would otherwise throw and break rendering.
      let target: Element | null = null;
      try {
        target = document.querySelector(hash);
      } catch {
        return false;
      }
      if (!target) return false;
      target.scrollIntoView({ behavior: hashBehavior, block: 'start' });
      return true;
    };

    if (scrollToHash()) return undefined;

    const observer = new MutationObserver(() => {
      if (scrollToHash()) {
        observer.disconnect();
        clearTimeout(timer);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    const timer = setTimeout(() => {
      observer.disconnect();
      // The hash pointed at nothing. Top of the page is the honest fallback.
      toTop();
    }, HASH_TARGET_TIMEOUT_MS);

    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [pathname, hash]);

  return null;
}
